import { toJSON, uid } from '@rehaje/shared'

import type { ClipboardOptions } from './types'
import type {
  ClipboardSpecific,
  ImportedSnapshot,
  SnapshotFilterType,
  Store,
  TypedNode
} from '@rehaje/core'

export class Clipboard {
  options: ClipboardOptions
  store?: Store

  get origin(): string {
    return this.options.origin!
  }

  constructor(options: ClipboardOptions = {}) {
    this.options = { origin: `__clipboard_origin__${uid()}__`, ...options }
  }

  /**
   * 复制，等价于
   * await clipboard.write(clipboard.toClipboard(...args))
   */
  async copy(
    nodes: TypedNode[] = [],
    filter?: (node: TypedNode, type: SnapshotFilterType) => boolean
  ): Promise<void> {
    const specific = this.toClipboard(nodes, filter)
    if (specific) await this.write(specific)
  }

  /**
   * 导入剪切板协议
   */
  fromClipboard(
    clipboard: ClipboardSpecific,
    withDependencies: boolean
  ): ImportedSnapshot | undefined {
    return this.store?.dependency.fromSnapshot(clipboard.snapshot, {
      withDependencies
    })
  }

  /**
   * 粘贴，等价于
   * clipboard.fromClipboard(await clipboard.read(), ...args)
   */
  async paste(withDependencies: boolean = true): Promise<void> {
    const clipboard = await this.read()
    if (clipboard) this.fromClipboard(clipboard, withDependencies)
  }

  /**
   * 从浏览器剪切板中读取剪切板协议
   */
  async read(): Promise<ClipboardSpecific | undefined> {
    try {
      const text = await navigator.clipboard.readText()
      const json = JSON.parse(text) as Partial<ClipboardSpecific> | undefined
      if (json?.type === 'Clipboard' && json?.snapshot?.sources?.length) {
        return json as ClipboardSpecific
      }
    } catch (error) {
      this.store?.raise(error as Error)
    }
  }

  /**
   * 导出剪切板协议
   */
  toClipboard(
    nodes: TypedNode[] = [],
    filter?: (node: TypedNode, type: SnapshotFilterType) => boolean
  ): ClipboardSpecific | undefined {
    const snapshot = this.store?.dependency.toSnapshot(nodes, filter)
    if (snapshot?.sources.length) {
      return { origin: this.origin, snapshot, type: 'Clipboard' }
    }
  }

  /**
   * 将剪切板协议写入到浏览器剪切板中
   */
  async write(clipboard?: ClipboardSpecific): Promise<void> {
    try {
      if (clipboard?.snapshot?.sources?.length) {
        const text = JSON.stringify(toJSON(clipboard))
        await navigator.clipboard.writeText(text)
      }
    } catch (error) {
      this.store?.raise(error as Error)
    }
  }
}
